import {
  ButtonType,
  CompoundButton,
  DefaultButton,
  DirectionalHint,
  IButtonProps as FIButtonProps,
  IButtonStyles,
  IconButton,
  IContextualMenuItem,
  IContextualMenuStyles,
  IIconStyles,
  ILabelStyles,
  ImageFit,
  Label,
  Stack,
  TooltipHost,
} from '@fluentui/react';
import { forwardRef, Ref } from 'react';

import { IComponent, IStyleFunctionOrObject } from '../../models/components.models';
import { contextualMenuStylesButton, contextualMenuStylesDefault, iconStylesOption } from '../../themes/themes';
import { useClassNames, useThemeStyles } from '../../utils/hooks';
import { ILoaderProps, Loader, LoaderType } from '../Loader/Loader';
import { loaderStylesProgressIndicatorButton, loaderStylesSpinnerButton } from '../Loader/Loader.styles';
import { ITooltipHostProps } from '../TooltipHost/TooltipHost';
import {
  buttonContainerStylesDefault,
  buttonLabelStylesDefault,
  buttonStylesDefault,
  IButtonContainerClassNames,
  IButtonContainerStyles,
} from './Button.styles';

// TODO: ButtonType is deprecated, Use specific button component instead
export interface IButtonProps extends Omit<FIButtonProps, 'styles'>, IComponent<IButtonStyles> {
  menuItems?: IButtonMenuItem[];
  menuDirectionalHint?: DirectionalHint;
  iconName?: string;
  menuIconName?: string;
  picture?: string;
  /**
   * If set to true, and if `menuProps` and `onClick` are provided, the button will render as a SplitButton.
   */
  split?: boolean;
  disabled?: boolean;
  htmlFor?: string;
  loading?: boolean;
  loaderWithMenuButton?: boolean;
  loaderProps?: ILoaderProps;
  iconStyles?: IStyleFunctionOrObject<IIconStyles> | IStyleFunctionOrObject<IIconStyles>[];
  buttonContainerStyles?:
    | IStyleFunctionOrObject<IButtonContainerStyles>
    | IStyleFunctionOrObject<IButtonContainerStyles>[];
  labelStyles?: IStyleFunctionOrObject<ILabelStyles> | IStyleFunctionOrObject<ILabelStyles>[];
  contextualMenuStyles?:
    | IStyleFunctionOrObject<IContextualMenuStyles>
    | IStyleFunctionOrObject<IContextualMenuStyles>[];
  tooltip?: string | JSX.Element | JSX.Element[];
}

export interface IButtonMenuItem extends IContextualMenuItem {
  /** Optional tooltip shown on hover. */
  tooltipProps?: ITooltipHostProps;
}

export const Button = forwardRef((props: IButtonProps, ref: Ref<HTMLDivElement>) => {
  const {
    styles,
    iconStyles,
    buttonContainerStyles,
    labelStyles,
    contextualMenuStyles,
    iconName,
    picture,
    menuItems,
    menuDirectionalHint,
    menuIconName,
    htmlFor,
    loading = false,
    loaderWithMenuButton = false,
    loaderProps = {},
    tooltip,
    ..._fProps
  } = props;
  const {
    className,
    iconProps,
    text,
    menuProps,
    menuIconProps,
    buttonType = ButtonType.default,
    primaryDisabled,
    onRenderText,
  } = props;
  const classNames = useClassNames<IButtonContainerStyles, IButtonContainerClassNames>(
    'ButtonContainer',
    buttonContainerStylesDefault,
    buttonContainerStyles,
    className,
    loading && 'loading',
    loaderWithMenuButton && 'loader-with-menu-button',
    !!primaryDisabled && 'primary-disabled'
  );
  const menuIconStyles = useThemeStyles<IIconStyles>(undefined, iconStyles || iconStylesOption);
  const menuStyles = useThemeStyles<IContextualMenuStyles>(
    contextualMenuStylesDefault,
    contextualMenuStyles || contextualMenuStylesButton
  );
  const buttonLabelStyles = useThemeStyles<Partial<ILabelStyles>>(buttonLabelStylesDefault, labelStyles);
  const items = menuItems
    ? menuItems.map((item) => {
        if (item.iconProps) {
          return {
            ...item,
            iconProps: {
              ...item.iconProps,
              styles: menuIconStyles,
            },
          };
        }
        return item;
      })
    : null;
  const fProps: FIButtonProps = {
    ..._fProps,
    buttonType,
    iconProps: iconProps
      ? iconProps
      : picture
      ? { imageProps: { src: picture, imageFit: ImageFit.cover } }
      : iconName
      ? { iconName }
      : undefined,
    menuProps: menuProps
      ? menuProps
      : items
      ? {
          items,
          directionalHint: menuDirectionalHint,
          styles: menuStyles,
          onRenderContextualMenuItem: (menuItem, defaultRender) => {
            if (!menuItem?.tooltipProps) return defaultRender?.(menuItem) ?? null;

            const { content, styles: tooltipStyles, ...restTooltipProps } = menuItem.tooltipProps;

            return (
              <TooltipHost
                {...restTooltipProps}
                content={<Stack style={{ padding: '0px 8px' }}>{content}</Stack>}
                styles={{
                  ...tooltipStyles,
                  root: { width: '100%', ...tooltipStyles?.root },
                }}
              >
                {defaultRender?.(menuItem)}
              </TooltipHost>
            );
          },
        }
      : undefined,
    menuIconProps: menuIconProps
      ? menuIconProps
      : menuIconName
      ? {
          iconName: menuIconName,
        }
      : undefined,
    styles: useThemeStyles<IButtonStyles>(buttonStylesDefault, styles),
    onRenderText: htmlFor
      ? () => (
          <Label htmlFor={htmlFor} styles={buttonLabelStyles}>
            {text}
          </Label>
        )
      : onRenderText,
  };
  const renderButton = () => {
    switch (buttonType) {
      case ButtonType.compound:
        return <CompoundButton {...fProps} />;
      case ButtonType.icon:
        return <IconButton {...fProps} />;
      default:
        return <DefaultButton {...fProps} />;
    }
  };
  const renderLoader = () => {
    if (loading) {
      const { styles, type } = loaderProps;
      return (
        <div className={classNames.loaderContainer}>
          <div className={classNames.loader} data-test="button-loader">
            <Loader
              {...loaderProps}
              styles={
                styles
                  ? styles
                  : type === LoaderType.progressIndicator
                  ? loaderStylesProgressIndicatorButton
                  : loaderStylesSpinnerButton
              }
            />
          </div>
        </div>
      );
    }
    return null;
  };
  return (
    <div className={classNames.root} ref={ref} data-test={props['data-test'] || 'button'}>
      {tooltip ? (
        <TooltipHost content={<Stack style={{ padding: '0px 8px' }}>{tooltip}</Stack>}>
          {renderButton()}
          {renderLoader()}
        </TooltipHost>
      ) : (
        <>
          {renderButton()}
          {renderLoader()}
        </>
      )}
    </div>
  );
});
